import { useEffect, useState } from "react";
import { api, notify } from "./api";
import type { ActionItem, ReviewDraft } from "../shared/updates";
export type Person = { id: string; name: string; role: string };
export const emptyReview = (): ReviewDraft => ({ mentions: [] });
export function ReviewControls({
  people,
  value,
  onChange,
}: {
  people: Person[];
  value: ReviewDraft;
  onChange: (v: ReviewDraft) => void;
}) {
  return (
    <details className="review-controls">
      <summary>Assign action</summary>
      <label>
        <input
          type="checkbox"
          checked={!!value.action}
          onChange={(e) =>
            onChange({
              ...value,
              action: e.target.checked
                ? { title: "", assignee: null, due: null }
                : undefined,
            })
          }
        />
        Assign action
      </label>
      {value.action && (
        <>
          <label>
            Action description
            <input
              maxLength={300}
              required
              value={value.action.title}
              onChange={(e) =>
                onChange({
                  ...value,
                  action: { ...value.action!, title: e.target.value },
                })
              }
            />
          </label>
          <label>
            Assignee
            <select
              value={value.action.assignee ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  action: {
                    ...value.action!,
                    assignee: e.target.value || null,
                  },
                })
              }
            >
              <option value="">Unassigned</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Due date (optional)
            <input
              type="date"
              value={value.action.due ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  action: { ...value.action!, due: e.target.value || null },
                })
              }
            />
          </label>
        </>
      )}
    </details>
  );
}
export function ThreadActions({
  board,
  thread,
  canChange,
  people,
}: {
  board: string;
  thread: string;
  canChange: boolean;
  people: Person[];
}) {
  const [state, setState] = useState<{
    actions: ActionItem[];
    history: { id: string; actor: string; description: string }[];
  }>({ actions: [], history: [] });
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    const refresh = async () => {
      if (document.hidden) return;
      try {
        const v = await api<typeof state>(
          `/api/rooms/${board}/actions?thread=${encodeURIComponent(thread)}`,
        );
        if (live) {
          setState(v);
          setError("");
        }
      } catch (e) {
        if (live) {
          setState({ actions: [], history: [] });
          setError((e as Error).message);
        }
      }
    };
    void refresh();
    const timer = setInterval(refresh, 5000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [board, thread]);
  const change = async (a: ActionItem, patch: Record<string, unknown>) => {
    try {
      await api(`/api/rooms/${board}/actions/${encodeURIComponent(a.id)}`, {
        version: a.version,
        ...patch,
      });
      setState(
        await api(
          `/api/rooms/${board}/actions?thread=${encodeURIComponent(thread)}`,
        ),
      );
    } catch (e) {
      notify((e as Error).message);
    }
  };
  return (
    <div className="thread-actions">
      {error && <p role="status">{error}</p>}
      {state.actions.map((a) => (
        <div key={a.id} data-review-action={a.id} data-review-updated={a.updated_at}>
          <label>
            <input
              type="checkbox"
              checked={!!a.done}
              disabled={!canChange}
              onChange={() => void change(a, { done: !a.done })}
            />
            {a.title}
          </label>
          <small>
            {a.done ? "Done" : "Open"}
            {a.due_date ? ` · Due ${a.due_date}` : ""}
          </small>
          <select
            aria-label="Action assignee"
            disabled={!canChange}
            value={a.assignee_id ?? ""}
            onChange={(e) =>
              void change(a, { assignee: e.target.value || null })
            }
          >
            <option value="">Unassigned</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      ))}
      {state.history.length > 0 && (
        <details>
          <summary>Action history</summary>
          {state.history.map((h) => (
            <small key={h.id}>
              {h.actor} · {h.description}
            </small>
          ))}
        </details>
      )}
    </div>
  );
}
